import { pickUserProfilePictureProps, UserProfilePicture } from "@common/components/ProfilePicture/component";
import { FormBlockInfo, FormBlockInfoItem } from "@common/controls/Form/Block/component";
import CancelButton from "@common/controls/Form/Buttons/Cancel";
import { FormButtons } from "@common/controls/Form/component";
import Hint from "@common/controls/Hint/component";
import Icon from "@common/controls/Icon/component";
import { IconType, type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { formatMoneyDisplay, formatNumberDisplay, formatPercentDisplay } from "@common/lib/format";
import { usePromise } from "@common/lib/hooks";
import { i18nFormat } from "@common/lib/i18n";
import { isNullOrUndefined, MAX_DB_INT } from "@common/lib/misc";
import { type Affiliate } from "@common/modules/affiliate/types";
import Network from "@common/modules/network/types";
import * as React from "react";

type Props = {
  api: Affiliate.AuditApi;
  audit: Network.CommissionRules.Api.CommissionAuditData;
  cycle: Affiliate.Cycle;
  isHost?: boolean;
  onDrillDown: (type: "Points" | "Volume" | "CommissionVolume") => void;
  onClose: () => void;
};
export default (props: Props) => {
  const i18n = useI18n();

  const [currencyVolumeTypes] = usePromise<OptionItem<string>[]>({
    fn: props.api.options.getVolumeTypes,
    args: [],
    shouldLoad: true
  });

  const volumeTypeDescription = React.useMemo(() => {
    const description: string[] = [];

    if (props.audit.includeBankedVolume) description.push(i18n.banked);

    if (props.audit.includePersonalRecruitVolume) description.push(i18n.personalRecruit);

    if (props.audit.includeTeamRecruitVolume) description.push(i18n.teamRecruit);

    return description.length ? description.join(", ") : null;
  }, [
    i18n,
    props.audit.includeBankedVolume,
    props.audit.includePersonalRecruitVolume,
    props.audit.includeTeamRecruitVolume
  ]);

  const percentage = React.useMemo(() => {
    if (isNullOrUndefined(props.audit.percentage)) return null;

    return (
      <span>
        {formatPercentDisplay(props.audit.percentage)}
        {!isNullOrUndefined(props.audit.startPersonalPoints) && (
          <Hint
            hint={` ${i18nFormat(i18n.valuePoints, [
              "VALUE",
              isNullOrUndefined(props.audit.endPersonalPoints) || props.audit.endPersonalPoints === MAX_DB_INT
                ? `${props.audit.startPersonalPoints}+`
                : `${props.audit.startPersonalPoints} - ${props.audit.endPersonalPoints}`
            ])}`}
          />
        )}
      </span>
    );
  }, [i18n, props.audit.percentage, props.audit.endPersonalPoints, props.audit.startPersonalPoints]);

  const downloadCsv = React.useCallback(
    (onClick: () => void) => {
      return (
        <div className="fr fg xs" style={{ color: "var(--blue)", cursor: "pointer" }} onClick={onClick}>
          <Icon icon={IconType.Download} />
          {i18n.downloadCsv}
        </div>
      );
    },
    [i18n]
  );

  return (
    <div className="content">
      <FormBlockInfo isNarrow isBorderless>
        <div className="user_info">
          <FormBlockInfoItem title={i18n.affiliate}>
            <UserProfilePicture {...pickUserProfilePictureProps(props.cycle)} />
          </FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.instance}>{props.audit.genealogyInstance}</FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.rank}>{props.audit.rankName}</FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.status}>
            {props.audit.statusName}
            {!isNullOrUndefined(props.audit.commissionPercentage) && (
              <Hint
                hint={` ${i18nFormat(i18n.valueCommission, [
                  "VALUE",
                  formatPercentDisplay(props.audit.commissionPercentage)
                ])}`}
              />
            )}
          </FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.volumeTierPoints}>
            {isNullOrUndefined(props.audit.volumeTierPoints) ? null : (
              <div className="fr jcsb">
                <a
                  style={{ cursor: "pointer", color: "var(--blue)" }}
                  onClick={() => {
                    props.onDrillDown("Points");
                  }}
                >
                  {props.audit.volumeTierPoints}
                </a>
                {downloadCsv(() =>
                  props.api.download.volumeTierPoints({
                    genealogyId: props.cycle.genealogyId,
                    cycleEndDate: props.cycle.cycleEndDate,
                    commissionRuleId: props.cycle.commissionRuleId
                  })
                )}
              </div>
            )}
          </FormBlockInfoItem>
        </div>

        <Hint
          style={{ marginTop: `calc(var(--gap) * 3/2)`, marginBottom: `calc(var(--gap) * 3/2)` }}
          hint={i18n.infoCommissionDetails}
        />

        <div className="commission_info">
          <FormBlockInfoItem title={i18n.genealogy}>{props.audit.genealogyTypeName}</FormBlockInfoItem>
          <FormBlockInfoItem title={!!props.audit.endLeg ? i18n.targeLegs : i18n.targetLeg}>
            {props.audit.endLeg ? `${props.audit.startLeg} - ${props.audit.endLeg}` : props.audit.startLeg}
          </FormBlockInfoItem>
          <FormBlockInfoItem title={!!props.audit.endLevel ? i18n.levels : i18n.level}>
            {props.audit.endLevel ? `${props.audit.startLevel} - ${props.audit.endLevel}` : props.audit.startLevel}
          </FormBlockInfoItem>
          <FormBlockInfoItem
            title={!!props.audit.endBreakawayLevel ? i18n.targetBreakawayLevels : i18n.targetBreakawayLevel}
          >
            {props.audit.endBreakawayLevel
              ? `${props.audit.startBreakawayLevel} - ${props.audit.endBreakawayLevel}`
              : props.audit.startBreakawayLevel}
          </FormBlockInfoItem>
          <FormBlockInfoItem
            title={!!props.audit.endDifferentialLevel ? i18n.targetDifferentialLevels : i18n.targetDifferentialLevel}
          >
            {props.audit.endDifferentialLevel
              ? `${props.audit.startDifferentialLevel} - ${props.audit.endDifferentialLevel}`
              : props.audit.startDifferentialLevel}
          </FormBlockInfoItem>
          <FormBlockInfoItem title={!!props.audit.endRankName ? i18n.targetRanks : i18n.targetRank}>
            {props.audit.endRankName
              ? `${props.audit.startRankName} - ${props.audit.endRankName}`
              : props.audit.startRankName}
          </FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.volumeType}>{volumeTypeDescription}</FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.sharePool}>{props.audit.sharePoolName}</FormBlockInfoItem>
          <FormBlockInfoItem title={props.audit.volumeTypeLabel}>
            {isNullOrUndefined(props.audit.volume) ? null : (
              <div className="fr jcsb">
                <a
                  style={{ cursor: "pointer", color: "var(--blue)" }}
                  onClick={() =>
                    props.audit.volumeTypeKey === "LeadershipCommission" || props.audit.volumeTypeKey === "Commission"
                      ? props.onDrillDown("CommissionVolume")
                      : props.onDrillDown("Volume")
                  }
                >
                  {(currencyVolumeTypes ?? []).map((x) => x.value).includes(props.audit.volumeTypeKey)
                    ? formatMoneyDisplay(props.audit.volume, props.audit.currencyCode)
                    : props.audit.volume}
                </a>
                {downloadCsv(() =>
                  props.audit.volumeTypeKey === "LeadershipCommission" || props.audit.volumeTypeKey === "Commission"
                    ? props.api.download.commissionVolume({
                        genealogyId: props.cycle.genealogyId,
                        cycleEndDate: props.cycle.cycleEndDate,
                        commissionRuleId: props.cycle.commissionRuleId
                      })
                    : props.api.download.commissionUserVolume({
                        genealogyId: props.cycle.genealogyId,
                        cycleEndDate: props.cycle.cycleEndDate,
                        commissionRuleId: props.cycle.commissionRuleId
                      })
                )}
              </div>
            )}
          </FormBlockInfoItem>
          <FormBlockInfoItem
            title={i18nFormat(i18n.bankedVolumeTypeLabel, [
              "VOLUMETYPELABEL",
              props.audit.volumeTypeLabel?.toLowerCase()
            ])}
          >
            {isNullOrUndefined(props.audit.bankedVolume)
              ? null
              : formatMoneyDisplay(props.audit.bankedVolume, props.audit.currencyCode)}
          </FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.shareValue}>
            {isNullOrUndefined(props.audit.shareValue)
              ? null
              : formatMoneyDisplay(props.audit.shareValue, props.audit.currencyCode, { digits: 4, trimZero: true })}
          </FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.percentage}>{percentage}</FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.basePayout}>
            {isNullOrUndefined(props.audit.basePayout)
              ? null
              : formatMoneyDisplay(props.audit.basePayout, props.audit.currencyCode)}
          </FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.minPayout}>
            {isNullOrUndefined(props.audit.minPayout)
              ? null
              : formatMoneyDisplay(props.audit.minPayout, props.audit.currencyCode)}
          </FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.maxPayout}>
            {isNullOrUndefined(props.audit.maxPayout)
              ? null
              : formatMoneyDisplay(props.audit.maxPayout, props.audit.currencyCode)}
          </FormBlockInfoItem>
          <FormBlockInfoItem title={i18n.payout}>
            {isNullOrUndefined(props.audit.genealogyCycleCommissionAmount) ? null : (
              <>
                {formatMoneyDisplay(props.audit.genealogyCycleCommissionAmount, props.audit.currencyCode)}
                {!isNullOrUndefined(props.audit.genealogyCycleCommissionShares) && (
                  <Hint
                    hint={` ${i18nFormat(i18n.valueShares, [
                      "VALUE",
                      formatNumberDisplay(props.audit.genealogyCycleCommissionShares, 4, undefined, true)
                    ])}`}
                  />
                )}
              </>
            )}
          </FormBlockInfoItem>
        </div>
      </FormBlockInfo>
      <FormButtons>
        <CancelButton onClick={props.onClose} caption={i18n.close} />
      </FormButtons>
    </div>
  );
};
